import React, {
  useState,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
} from "react";
import DeletePopUp from "../DeletePopUp";
import { Pencil } from "lucide-react";
import { ICategory, IDropdownOption } from "../../exports/interfaces";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { fetchDropdownCategories } from "../../api/AdminApi";
import { useLoader } from "../../context/LoaderContext";
import { usePermission } from "../../hooks/usePermission";
import { useLocation } from "react-router-dom";

export const getStatusClass = (status: string) => {
  switch (status) {
    case "Active":
      return "pill-success";
    case "Inactive":
      return "pill-danger";
    case "Deactivated":
      return "bg-secondary";
    default:
      return "bg-secondary";
  }
};

const Dropdown = ({
  categories,
  setCategories,
}: {
  categories: ICategory[];
  setCategories: Dispatch<SetStateAction<ICategory[]>>;
}) => {
  const { setLoading } = useLoader();
  const { canCreate, canEdit, canDelete } = usePermission();
  const location = useLocation();

  const sectionId = location.state?.sectionId;
  const [selectedCategory, setSelectedCategory] = useState<ICategory | null>(
    null,
  );
  const [tableRows, setTableRows] = useState<IDropdownOption[]>([]);
  const [showDelete, setShowDelete] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const [newValue, setNewValue] = useState<string>("");
  const [newLabel, setNewLabel] = useState<string>("");
  const [newOrder, setNewOrder] = useState<string>("");
  const [newStatus, setNewStatus] = useState<string>("Active");
  const [newErrors, setNewErrors] = useState<{
    label: string;
    value: string;
    order: string;
  }>({
    label: "",
    value: "",
    order: "",
  });
  const [editErrors, setEditErrors] = useState<{
    label: string;
    value: string;
  }>({
    label: "",
    value: "",
  });

  // Track if we've initialized selectedCategory
  const initialized = useRef(false);

  const handleCategorySelect = (category: ICategory) => {
    setSelectedCategory(category);
    setTableRows(category.dropdowns);
    const maxOrder =
      category.dropdowns && category.dropdowns.length
        ? Math.max(...category.dropdowns.map((d) => Number(d.order || 0)))
        : 0;
    setNewOrder(String(maxOrder + 1));
  };

  useEffect(() => {
    if (categories.length > 0 && !initialized.current) {
      initialized.current = true;

      const selected =
        categories.find((category) => category.name === sectionId) ||
        categories[0];

      setSelectedCategory(selected);
      setTableRows(selected.dropdowns || []);

      const maxOrder =
        selected.dropdowns && selected.dropdowns.length
          ? Math.max(...selected.dropdowns.map((d) => Number(d.order || 0)))
          : 0;

      setNewOrder(String(maxOrder + 1));
    }
  }, [categories, sectionId]);

  const handleRowChange = (
    id: number,
    field: "label" | "value",
    value: string,
  ) => {
    setTableRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)),
    );
  };

  const getDropdownErrors = (label: string, value: string, order?: string) => {
    const errors = { label: "", value: "", order: "" };

    if (!label.trim()) {
      errors.label = "Label is required.";
    }

    if (!value.trim()) {
      errors.value = "Value is required.";
    }

    if (order !== undefined) {
      if (!order.trim()) {
        errors.order = "Sort order is required.";
      } else {
        const orderNumber = Number(order);
        if (Number.isNaN(orderNumber) || orderNumber < 0) {
          errors.order = "Sort order must be a non-negative number.";
        }
      }
    }

    return errors;
  };

  const validateDropdownFields = (
    label: string,
    value: string,
    order?: string,
    setErrors?: boolean,
  ): boolean => {
    const errors = getDropdownErrors(label, value, order);

    if (setErrors) {
      setNewErrors(errors);
    }

    if (errors.label || errors.value || errors.order) {
      if (!setErrors) {
        dynamicFailed(errors.label || errors.value || errors.order);
      }
      return false;
    }

    return true;
  };

  const handleClose = () => {
    setShowDelete(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(
        `/options/dropdowns/${deleteId}`,
      );
      if (response.status === 200 || response.status === 201) {
        const categoryData = await fetchDropdownCategories();
        setCategories(categoryData);
        const updatedCategory =
          categoryData.find((c) => c.id === selectedCategory?.id) || null;
        setSelectedCategory(updatedCategory);
        setTableRows(updatedCategory ? updatedCategory.dropdowns : []);
        setShowDelete(false);
        setDeleteId(null);
        setTimeout(() => {
          setLoading(false);
          dynamicSuccess("Dropdown option deleted successfully!");
        }, 1000);
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "Something went wrong!";
      dynamicFailed(message);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleSaveEdit = async (rowId: number) => {
    const editedRow = tableRows.find((r) => r.id === rowId);
    if (!editedRow) return;

    const errors = getDropdownErrors(editedRow.label, editedRow.value);
    if (errors.label || errors.value) {
      setEditErrors({ label: errors.label, value: errors.value });
      return;
    }

    const payload = {
      label: editedRow.label,
      value: editedRow.value,
      order: editedRow.order,
      status: editedRow.status,
    };

    try {
      setLoading(true);
      const response = await axiosClient.put(
        `/options/dropdowns/${rowId}`,
        payload,
      );

      if (response.status === 200 || response.status === 201) {
        const categoryData = await fetchDropdownCategories();
        setCategories(categoryData);
        const updatedCategory =
          categoryData.find((c) => c.id === selectedCategory?.id) || null;
        setSelectedCategory(updatedCategory);
        setTableRows(updatedCategory ? updatedCategory.dropdowns : []);
        setEditingId(null);

        dynamicSuccess("Dropdown option updated successfully!");
      } else {
        dynamicFailed("Failed to update option");
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Something went wrong";
      dynamicFailed(message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleSubmit = async () => {
    if (!selectedCategory) {
      dynamicFailed("Please select a category first!");
      return;
    }

    if (!validateDropdownFields(newLabel, newValue, newOrder, true)) {
      return;
    }

    const payload = {
      label: newLabel,
      value: newValue,
      order: newOrder !== "" ? Number(newOrder) : undefined,
      status: newStatus,
      categoryId: selectedCategory.id,
    };

    try {
      setLoading(true);
      const response = await axiosClient.post("/options/dropdowns", payload);

      if (response.status === 200 || response.status === 201) {
        const categoryData = await fetchDropdownCategories();
        setCategories(categoryData);
        const updatedCategory =
          categoryData.find((c) => c.id === selectedCategory.id) || null;
        setSelectedCategory(updatedCategory);
        setTableRows(updatedCategory ? updatedCategory.dropdowns : []);
        setNewLabel("");
        setNewValue("");
        setNewOrder("");
        setNewStatus("Active");
        setShowForm(false);

        dynamicSuccess("Dropdown option added successfully!");
      } else {
        dynamicFailed("Failed to add option");
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message?.[0] ||
        error?.response?.data?.message ||
        "Something went wrong";
      dynamicFailed(message);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  return (
    <>
      <div className="row g-3">
        {/* Left Sidebar */}
        <div className="col-lg-3 col-md-5">
          <div className="dashboard-card h-100 p-0 overflow-hidden">
            <h5 className="primary-color p-2 border-bottom mb-0">Categories</h5>

            <div className="list-group list-group-flush custom-list-group">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`list-group-item list-group-item-action border-0 border-bottom text-start ${
                    selectedCategory?.id === category.id
                      ? "active-category"
                      : ""
                  }`}
                  onClick={() => handleCategorySelect(category)}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Content */}
        <div className="col-lg-9 col-md-7">
          <div id={selectedCategory?.name} className="dashboard-card">
            <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-2">
              <div>
                <h5 className="primary-color">{selectedCategory?.name}</h5>
                <small>Manage available options for this dropdown field</small>
              </div>

              {canCreate("Admin Control") && (
                <button
                  className="btn btn-badge-primary px-3"
                  onClick={() => {
                    if (selectedCategory) {
                      const maxOrder =
                        selectedCategory.dropdowns &&
                        selectedCategory.dropdowns.length
                          ? Math.max(
                              ...selectedCategory.dropdowns.map((d) =>
                                Number(d.order || 0),
                              ),
                            )
                          : 0;
                      setNewOrder(String(maxOrder + 1));
                    }
                    setShowForm((prev) => !prev);
                  }}
                >
                  <i className="fa-solid fa-plus me-2"></i>
                  Add Option
                </button>
              )}
            </div>
            <div
              className={`option-form-wrapper dashboard-card ${showForm ? "open" : "p-0"}`}
            >
              <div className="option-form">
                <div className="row g-3">
                  <div className="col-md-6 col-lg-3">
                    <label className="form-label">Value (Slug)</label>
                    <input
                      type="text"
                      className={`form-control ${newErrors.value ? "input-error" : ""}`}
                      placeholder="e.g Agriculture"
                      value={newValue}
                      onChange={(e) => {
                        setNewValue(e.target.value);
                        if (newErrors.value) {
                          setNewErrors((prev) => ({ ...prev, value: "" }));
                        }
                      }}
                    />
                    {newErrors.value && (
                      <div className="text-error">{newErrors.value}</div>
                    )}
                  </div>

                  <div className="col-md-6 col-lg-3">
                    <label className="form-label">Label (Display)</label>
                    <input
                      type="text"
                      className={`form-control ${newErrors.label ? "input-error" : ""}`}
                      placeholder="e.g Agriculture"
                      value={newLabel}
                      onChange={(e) => {
                        setNewLabel(e.target.value);
                        if (newErrors.label) {
                          setNewErrors((prev) => ({ ...prev, label: "" }));
                        }
                      }}
                    />
                    {newErrors.label && (
                      <div className="text-error">{newErrors.label}</div>
                    )}
                  </div>

                  <div className="col-md-12 col-lg-3">
                    <label className="form-label">Sort Order</label>
                    <input
                      type="number"
                      min={0}
                      className={`form-control ${newErrors.order ? "input-error" : ""}`}
                      value={newOrder}
                      onChange={(e) => {
                        setNewOrder(e.target.value);
                        if (newErrors.order) {
                          setNewErrors((prev) => ({ ...prev, order: "" }));
                        }
                      }}
                    />
                    {newErrors.order && (
                      <div className="text-error">{newErrors.order}</div>
                    )}
                  </div>
                </div>

                <div className="d-flex justify-content-end gap-2 mt-3">
                  <button
                    className="btn btn-badge-border px-3"
                    onClick={() => {
                      setShowForm(false);
                      setNewLabel("");
                      setNewValue("");
                      setNewOrder("");
                      setNewStatus("Active");
                      setNewErrors({ label: "", value: "", order: "" });
                    }}
                  >
                    Cancel
                  </button>

                  <button
                    className="btn btn-badge-primary px-3"
                    onClick={handleSubmit}
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
            <div className="table-responsive admin-table table-checkbox dropdown-fix-column">
              <table className="table align-middle">
                <thead className="table-light">
                  <tr>
                    <th>Label</th>
                    <th>Value</th>
                    <th>Order</th>
                    <th>Status</th>
                    <th
                      className="sticky-action"
                      style={{ backgroundColor: "var(--white) !important" }}
                    >
                      Action
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {tableRows.map((row) => (
                    <tr key={row.id}>
                      <td>
                        {editingId === row.id ? (
                          <>
                            <input
                              type="text"
                              className={`form-control ${editErrors.label ? "input-error" : ""}`}
                              value={row.label}
                              onChange={(e) => {
                                handleRowChange(
                                  row.id,
                                  "label",
                                  e.target.value,
                                );
                                if (editErrors.label) {
                                  setEditErrors((prev) => ({
                                    ...prev,
                                    label: "",
                                  }));
                                }
                              }}
                            />
                            {editErrors.label && (
                              <div className="text-error">
                                {editErrors.label}
                              </div>
                            )}
                          </>
                        ) : (
                          row.label
                        )}
                      </td>

                      <td>
                        {editingId === row.id ? (
                          <>
                            <input
                              type="text"
                              className={`form-control ${editErrors.value ? "input-error" : ""}`}
                              value={row.value}
                              onChange={(e) => {
                                handleRowChange(
                                  row.id,
                                  "value",
                                  e.target.value,
                                );
                                if (editErrors.value) {
                                  setEditErrors((prev) => ({
                                    ...prev,
                                    value: "",
                                  }));
                                }
                              }}
                            />
                            {editErrors.value && (
                              <div className="text-error">
                                {editErrors.value}
                              </div>
                            )}
                          </>
                        ) : (
                          row.value
                        )}
                      </td>

                      <td>{row.order}</td>

                      <td>
                        <span
                          className={`badge rounded-pill px-3 py-2 ${getStatusClass(
                            row.status,
                          )}`}
                        >
                          {row.status}
                        </span>
                      </td>

                      <td className="sticky-action">
                        <div className="d-flex gap-3 align-items-center">
                          {editingId === row.id ? (
                            <>
                              <i
                                className={`fa-solid fa-floppy-disk text-success ${
                                  canEdit("Admin Control")
                                    ? "cursor-pointer"
                                    : "opacity-50"
                                }`}
                                title={
                                  canEdit("Admin Control")
                                    ? "Save"
                                    : "No permission"
                                }
                                style={{
                                  pointerEvents: canEdit("Admin Control")
                                    ? "auto"
                                    : "none",
                                }}
                                onClick={() => {
                                  if (!canEdit("Admin Control")) return;
                                  handleSaveEdit(row.id);
                                }}
                              ></i>

                              <i
                                className={`fa-solid fa-trash text-danger ${
                                  canDelete("Admin Control")
                                    ? "cursor-pointer"
                                    : "opacity-50"
                                }`}
                                title={
                                  canDelete("Admin Control")
                                    ? "Delete"
                                    : "No permission"
                                }
                                style={{
                                  pointerEvents: canDelete("Admin Control")
                                    ? "auto"
                                    : "none",
                                }}
                                onClick={() => {
                                  if (!canDelete("Admin Control")) return;
                                  setDeleteId(row?.id || 0);
                                  setShowDelete(true);
                                }}
                              ></i>
                            </>
                          ) : (
                            <>
                              <i
                                className={`cursor-pointer ${
                                  canEdit("Admin Control") ? "" : "opacity-50"
                                }`}
                                title={
                                  canEdit("Admin Control")
                                    ? "Edit"
                                    : "No permission"
                                }
                                style={{
                                  pointerEvents: canEdit("Admin Control")
                                    ? "auto"
                                    : "none",
                                }}
                                onClick={() => {
                                  if (!canEdit("Admin Control")) return;
                                  setEditingId(row.id);
                                  setEditErrors({ label: "", value: "" });
                                }}
                              >
                                <Pencil size={20} />
                              </i>

                              <i
                                className={`fa-solid fa-trash text-danger ${
                                  canDelete("Admin Control")
                                    ? "cursor-pointer"
                                    : "opacity-50"
                                }`}
                                title={
                                  canDelete("Admin Control")
                                    ? "Delete"
                                    : "No permission"
                                }
                                style={{
                                  pointerEvents: canDelete("Admin Control")
                                    ? "auto"
                                    : "none",
                                }}
                                onClick={() => {
                                  if (!canDelete("Admin Control")) return;
                                  setDeleteId(row?.id || 0);
                                  setShowDelete(true);
                                }}
                              ></i>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
    </>
  );
};

export default Dropdown;
